'use client';

import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Checkbox,
  MenuItem,
  Container,
  Autocomplete,
  Switch,
  Select,
  OutlinedInput,
  Chip,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { SPACING } from '@/app/constants/layout-constants';
import { COLORS } from '../theme';
import DateField from './DatePicker';
import TimeField from './TimePicker';
import NumberField from './NumberField';
import { StandardUploadInput } from './StandardUploadInput';
import { RESOURCE_TYPE } from '@/stack-shared/constants/resource-constants';

export enum GENERIC_FORM_FIELD_TYPE {
  TEXT = 'text',
  FLOAT = 'float',
  MULTI_SELECT = 'multi-select',
  SINGLE_SELECT = 'single-select',
  CHECKBOX = 'checkbox',
  MULTI_CHECKBOX = 'multi-checkbox',
  SWITCH = 'switch',
  EMAIL = 'email',
  PASSWORD = 'password',
  RECAPTCHA = 'recaptcha',
  REACT_ELEMENT = 'react-element',
  HIDDEN = 'hidden',
  GROUP = 'group',
  AUTOCOMPLETE = 'autocomplete',
  DATE = 'date',
  TIME = 'time',
  NUMBER = 'number',
  FILE = 'file'
}

export interface GenericFormFieldOption {
  label: string;
  value: string | number;
}

export interface GenericFormFieldSchema {
  label: string | React.ReactNode;
  inlineNode?: React.ReactNode;
  name: string;
  type: string;
  options?: GenericFormFieldOption[];
  fetchOptions?: (query: string) => Promise<GenericFormFieldOption[]>; // For server-side fetching
  required?: boolean;
  validation?: any;
  showConditions?: { key: string; value: any }[]; // Array of conditions for showing the field
  columns?: number; // Determines field width within the form (1-12 mapping)
  component?: React.ReactNode; // Custom React component
  disableFutureDays?: boolean; // Disable future dates in date picker
  disabled?: boolean
}

export interface GenericFormGroup {
  groupName?: string;
  ContainerComponent?: React.FC<{ children: React.ReactNode }>; // Optional wrapper for the group
  fields: GenericFormFieldSchema[];
}

export interface GenericFormProps {
  onSubmit: (values: any) => void;
  onCancel?: () => void;
  initialValues: Record<string, any>;
  groups: GenericFormGroup[]; // Groups instead of a flat list of fields
  title?: string;
  submitLabel?: string;
  enableReinitialize?: boolean;
  showCancelButton?: boolean;
  recaptchaKey?: number;
  formWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false; // Material-UI breakpoint for form width
  buttonContainerStyle?: React.CSSProperties;
  formStyle?: React.CSSProperties;
}

const labelTypography = (
  label: string | React.ReactNode
) => (
  <Typography
    variant="body1"
    sx={{
      marginBottom: '4px',
      fontWeight: 'bold',
    }}
  >
    {label}
  </Typography>
);

const generateValidationSchema = (groups: GenericFormGroup[]) => {
  const shape: Record<string, any> = {};
  groups.forEach((group) => {
    group.fields.forEach((field) => {
      if (field.type === GENERIC_FORM_FIELD_TYPE.REACT_ELEMENT) {
        return;
      }
      if (field.validation) {
        shape[field.name] = field.validation;
      } else if (field.required) {
        if (field.type === 'checkbox') {
          shape[field.name] = Yup.boolean().oneOf([true], 'This field must be checked');
        } else {
          shape[field.name] = Yup.string().required('This field is required');
        }
      }
    });
  });
  return Yup.object().shape(shape);
};

const GenericForm: React.FC<GenericFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  groups,
  title,
  submitLabel = 'Submit',
  enableReinitialize = false,
  showCancelButton = false,
  recaptchaKey,
  formWidth = 'md',
  buttonContainerStyle = {
    marginTop: SPACING.lg,
  },
  formStyle = {
    margin: '0 auto', padding: 0 
  }
}) => {
  const validationSchema = generateValidationSchema(groups);

  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [optionsState, setOptionsState] = React.useState<Record<string, any[]>>({});



  const renderField = (
    field: GenericFormFieldSchema,
    handleChange: any,
    handleBlur: any,
    values: any,
    errors: any,
    touched: any,
    setFieldValue: any,
    validateForm: any
  ) => {

    const handleFetchOptions = async (query: string) => {
      if (field.fetchOptions && query.length > 2) {
        try {
          const dynamicOptions = await field.fetchOptions(query);

          // Update options dynamically for this field
          setOptionsState((prevState) => ({
            ...prevState,
            [field.name]: dynamicOptions,
          }));
        } catch (error) {
          console.error(`Error fetching options for ${field.name}:`, error);
        }
      }
    };

    switch (field.type) {
      case GENERIC_FORM_FIELD_TYPE.TEXT:
        return (
          <TextField
            disabled={field.disabled || false}
            fullWidth
            key={field.name}
            label={field.label}
            name={field.name}
            value={values[field.name] || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] ? <span style={{ color: COLORS.error }}>{errors[field.name]}</span> : ''}
          />
        );
      case GENERIC_FORM_FIELD_TYPE.EMAIL:
      case GENERIC_FORM_FIELD_TYPE.PASSWORD:
        return (
          <TextField
            style={{ marginBottom: SPACING.md }}
            fullWidth
            name={field.name}
            type={field.type}
            value={values[field.name] || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] ? <span style={{ color: COLORS.error }}>{errors[field.name]}</span> : ''}
          />
        );
      case GENERIC_FORM_FIELD_TYPE.CHECKBOX:
        return (
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values[field.name] || false}
                  onChange={(e) => {
                    setFieldValue(field.name, e.target.checked)
                    setTimeout(() => validateForm(), 0);
                  }}
                />
              }
              label={field.inlineNode || field.label}
            />
            {touched[field.name] && errors[field.name] && (
              <Typography
                variant="caption"
                style={{ color: COLORS.error, marginTop: 4, display: "block" }}
              >
                {errors[field.name]}
              </Typography>
            )}
          </div>
        );
      case GENERIC_FORM_FIELD_TYPE.SWITCH:
        return (
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={values[field.name] || false}
                  onChange={(e) => {
                    setFieldValue(field.name, e.target.checked)
                    setTimeout(() => validateForm(), 0);
                  }}
                />
              }
              label={field.inlineNode || field.label}
            />
            {touched[field.name] && errors[field.name] && (
              <Typography
                variant="caption"
                style={{ color: COLORS.error, marginTop: 4, display: "block" }}
              >
                {errors[field.name]}
              </Typography>
            )}
          </div>
        );
      case GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT:
        return (
          <TextField
            fullWidth
            select
            value={values[field.name] || ''}
            onChange={(e) => setFieldValue(field.name, e.target.value)}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] ? <span style={{ color: COLORS.error }}>{errors[field.name]}</span> : ''}
          >
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case GENERIC_FORM_FIELD_TYPE.MULTI_SELECT: {
        const opts = field.options ?? [];
        const coerceNumeric =
          opts.length > 0 && opts.every((o) => typeof o.value === 'number');
        const raw = values[field.name];
        const asArray = Array.isArray(raw)
          ? raw
          : raw !== undefined && raw !== null && raw !== ''
            ? [raw]
            : [];
        const selected = coerceNumeric
          ? asArray.map((v) => Number(v)).filter((n) => Number.isFinite(n))
          : asArray;
        return (
          <FormControl fullWidth error={Boolean(errors[field.name])}>
            <InputLabel id={`${field.name}-multi-label`}>{field.label}</InputLabel>
            <Select
              labelId={`${field.name}-multi-label`}
              multiple
              label={field.label as string}
              value={selected}
              onChange={(e) => {
                const v = e.target.value;
                const next = Array.isArray(v) ? v : v != null && v !== '' ? [v] : [];
                const coerced = coerceNumeric
                  ? next.map((x) => Number(x)).filter((n) => Number.isFinite(n))
                  : next;
                setFieldValue(field.name, coerced);
              }}
              input={<OutlinedInput label={field.label as string} />}
              renderValue={(sel) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(sel as (string | number)[]).map((v) => {
                    const opt = opts.find(
                      (o) => o.value === v || String(o.value) === String(v),
                    );
                    return (
                      <Chip key={String(v)} size="small" label={opt?.label ?? String(v)} />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                slotProps: {
                  paper: { sx: { maxHeight: 300 } },
                },
              }}
            >
              {opts.map((option) => (
                <MenuItem key={String(option.value)} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors[field.name] ? (
              <Typography
                variant="caption"
                style={{ color: COLORS.error, marginTop: 4, display: 'block' }}
              >
                {errors[field.name]}
              </Typography>
            ) : null}
          </FormControl>
        );
      }
      case GENERIC_FORM_FIELD_TYPE.RECAPTCHA:
        return (
          <div style={{ marginBottom: SPACING.lg }}>
            <ReCAPTCHA
              key={recaptchaKey}
              sitekey={import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY || ''}
              onChange={(value: string | null) => setFieldValue(field.name, value)}
            />
          </div>

        );
      case GENERIC_FORM_FIELD_TYPE.AUTOCOMPLETE:
        return (
          <Autocomplete
            style={{ marginBottom: SPACING.md }}
            options={optionsState[field.name] || []} // Use the dynamic state for options
            getOptionLabel={(option) => option.label || ''} // Ensure labels are defined
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(event, newValue) => setFieldValue(field.name, newValue)}
            onInputChange={(event, query) => handleFetchOptions(query)} // Fetch options dynamically
            value={values[field.name] || null}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                // label={field.label}
                error={Boolean(errors[field.name])}
                helperText={
                  errors[field.name]
                    ? <span style={{ color: 'red' }}>{errors[field.name]}</span>
                    : null
                }
              />
            )}
          />
        );
      case GENERIC_FORM_FIELD_TYPE.REACT_ELEMENT:
        return (
          <div style={{ marginBottom: SPACING.md }}>
            {field.component}
          </div>
        )
      case GENERIC_FORM_FIELD_TYPE.DATE:
        return (
          <DateField
            label={field.label as string}
            name={field.name}
            value={values[field.name] || null}
            onChange={(value) => setFieldValue(field.name, value)}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] ?? ''}
            disableFutureDays={field.disableFutureDays}
          />
        );

      case GENERIC_FORM_FIELD_TYPE.TIME:
        return (
          <TimeField
            label={field.label as string}
            name={field.name}
            value={values[field.name] || null}
            onChange={(value) => setFieldValue(field.name, value)}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] ?? ''}
          />
        );

      case GENERIC_FORM_FIELD_TYPE.FLOAT:
        return (
          <NumberField
            label={field.label as string}
            name={field.name}
            value={values[field.name] || ''}
            onChange={(value) => setFieldValue(field.name, value)}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] ?? ''}
            roundToDecimals={2}
            min={field.name === 'commissionPercentage' ? 0 : undefined}
            max={field.name === 'commissionPercentage' ? 100 : undefined}
          />
        );
      case GENERIC_FORM_FIELD_TYPE.NUMBER:
        return (
          <NumberField
            label={field.label as string}
            name={field.name}
            value={values[field.name] || ''}
            onChange={(value) => setFieldValue(field.name, value)}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] ?? ''}
          />
        );
      case GENERIC_FORM_FIELD_TYPE.FILE:
        return (
          <StandardUploadInput
            showPreview={true}
            onFileSelected={(files) => setFieldValue(field.name, files[0])}
            handleSubmit={(file) => setFieldValue(field.name, file)}
            initialImage={values[field.name]?.signedUrl || ''}
            accept={field.options?.some(opt => opt.value === RESOURCE_TYPE.IMAGE) ? 'image/*' : '*'}
            uploadType={field.options?.some(opt => opt.value === RESOURCE_TYPE.IMAGE) ? RESOURCE_TYPE.IMAGE : RESOURCE_TYPE.OTHER}
          />
        );


      default:
        return null;
    }
  };

  return (
    <Container maxWidth={formWidth} style={{ ...formStyle}}>
      {title && (
        <Typography variant="h6" style={{ marginBottom: '16px' }}>
          {title}
        </Typography>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={hasSubmitted}
        validateOnChange={false}
        onSubmit={async (values, { setTouched, validateForm }) => {
          // Mark all fields as touched
          const allFieldsTouched = Object.keys(initialValues).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          );

          await setTouched(allFieldsTouched); // Mark all fields as touched

          const errors = await validateForm(); // Validate the form explicitly

          if (Object.keys(errors).length === 0) {
            // If no validation errors, proceed with form submission
            onSubmit(values);
          } else {
            console.log("Form has validation errors, won't submit");
          }
        }}
        enableReinitialize={enableReinitialize}
      >
        {({ handleChange, handleBlur, handleSubmit, values, errors, touched, setTouched, validateForm, setFieldValue }) => {

          const handleCustomSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
            setHasSubmitted(true)
            event.preventDefault();

            // Mark all fields as touched
            const allFieldsTouched = Object.keys(initialValues).reduce(
              (acc, key) => ({ ...acc, [key]: true }),
              {}
            );

            await setTouched(allFieldsTouched); // Mark fields as touched

            const errors = await validateForm(); // Explicitly validate the form

            console.log("Form Errors:", errors);
            if (Object.keys(errors).length === 0) {
              handleSubmit(event); // Only call handleSubmit if no errors
            }
          };

          return (

            <form onSubmit={handleCustomSubmit} autoComplete="off">
                {groups.map((group, groupIndex) => {
                  const Container = React.useMemo(() => group.ContainerComponent || React.Fragment, [group.ContainerComponent]);

                  return (
                    <Container>
                      <Grid container spacing={2}>

                        {group.fields
                          .filter((field) => {
                            // Check if `showConditions` is satisfied
                            if (field.showConditions) {
                              return field.showConditions.every(
                                (condition) => values[condition.key] === condition.value
                              );
                            }
                            return true; // Default: show the field if no conditions are defined
                          })
                          .map((field, fieldIndex) => (
                            field.type === 'hidden' ? null :
                                <Grid
                                key={field.name || `field-${fieldIndex}`}
                                size={{ xs: 12, sm: field.columns || 12 }}
                               
                              >
                                {field.type === GENERIC_FORM_FIELD_TYPE.MULTI_SELECT
                                  ? null
                                  : labelTypography(field.label)}
                                {renderField(field, handleChange, handleBlur, values, errors, touched, setFieldValue, validateForm)}
                              </Grid>
                          ))}
                      </Grid>
                    </Container>
                  );
                })}

              <Grid container style={{ ...buttonContainerStyle }}>
                  {showCancelButton && (
                    <Grid size={{ xs: 12 }}>
                      <Button variant="outlined" onClick={onCancel} fullWidth style={{ textTransform: 'none', height: 40 }}>
                        Cancel
                      </Button>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12 }}>
                    <Button variant="contained" color="primary" type="submit" fullWidth style={{ textTransform: 'none', height: 40 }}>
                      {submitLabel}
                    </Button>
                  </Grid>
                </Grid> 
            </form>
          )
        }}
      </Formik>
    </Container>
  );
};



export default GenericForm;
